"use client";

import { type MotionProps, motion } from "framer-motion";
import React from "react";
import { cn } from "@/lib/utils";
import ResponsiveWrapper, {
  type ResponsiveProperties,
} from "./responsive-wrapper.tsx";

type Direction = "row" | "column" | "row-reverse" | "column-reverse";
type Justify = "start" | "center" | "end" | "between" | "around" | "evenly";
type Items = "start" | "center" | "end" | "stretch" | "baseline";
type Wrap = "nowrap" | "wrap" | "wrap-reverse";

const directionMap: Record<Direction, string> = {
  row: "flex-row",
  column: "flex-col",
  "row-reverse": "flex-row-reverse",
  "column-reverse": "flex-col-reverse",
};

const justifyMap: Record<Justify, string> = {
  start: "justify-start",
  center: "justify-center",
  end: "justify-end",
  between: "justify-between",
  around: "justify-around",
  evenly: "justify-evently",
};

const itemsMap: Record<Items, string> = {
  start: "items-start",
  center: "items-center",
  end: "items-end",
  stretch: "items-stretch",
  baseline: "items-baseline",
};

const wrapMap: Record<Wrap, string> = {
  nowrap: "flex-nowrap",
  wrap: "flex-wrap",
  "wrap-reverse": "flex-wrap-reverse",
};

// TODO: Build out the following
// Stack
// Divider
// ZStack
// Aspect Ratio

interface FlexProperties extends MotionProps, ResponsiveProperties {
  direction?: Direction;
  justify?: Justify;
  items?: Items;
  gap?: string;
  wrap?: Wrap;
  className?: string;
}

const Flex = React.forwardRef<HTMLDivElement, FlexProperties>(
  (
    {
      direction = "row",
      justify = "center",
      items = "center",
      gap = "gap-0",
      wrap = "nowrap",
      sm,
      md,
      lg,
      className = "",
      children,
      ...props
    },
    ref,
  ) => {
    const baseClasses = cn(
      "flex",
      directionMap[direction],
      justifyMap[justify],
      itemsMap[items],
      gap,
      wrapMap[wrap],
      className,
    );

    return (
      <ResponsiveWrapper className={baseClasses} sm={sm} md={md} lg={lg}>
        <motion.div ref={ref} {...props}>
          {children}
        </motion.div>
      </ResponsiveWrapper>
    );
  },
);

Flex.displayName = "Flex";

export default Flex;
